import React, { useMemo } from 'react'
import { DcChart, DcDiv, DcFlexBox, DcText, colors } from 'pixel-base'
import { InventoryProduct } from './types'

interface MostCountChartProps {
  data: InventoryProduct[]
  loading: boolean
  error: string | null
  onProductClick?: (product: InventoryProduct) => void
  showBarChartOnly?: boolean
  onTitleClick?: () => void
}

const MostCountChart: React.FC<MostCountChartProps> = ({
  data,
  loading,
  error,
  onProductClick: _onProductClick,
  showBarChartOnly = false,
  onTitleClick,
}) => {
  // Transform data for horizontal bar chart
  const barChartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        yAxis: [],
        series: [
          {
            name: 'Current Stock',
            type: 'bar',
            data: [],
            itemStyle: {
              color: colors.success,
            },
          },
        ],
      }
    }

    const products = data.slice(0, 10) // Show top 10 products
    const yAxis = products.map((product) => product.product_name)
    const seriesData = products.map((product) => ({
      value: product.total_current_stock || 0,
      itemStyle: {
        color: colors.success,
      },
    }))

    return {
      yAxis,
      series: [
        {
          name: 'Current Stock',
          type: 'bar',
          data: seriesData,
          itemStyle: {
            color: colors.success,
          },
        },
      ],
    }
  }, [data])

  // Transform data for donut chart (inventory value distribution)
  const donutChartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        series: [
          {
            name: 'Inventory Value',
            type: 'pie',
            radius: ['40%', '70%'],
            data: [],
            itemStyle: {
              color: colors.success,
            },
          },
        ],
      }
    }

    const products = data.slice(0, 8) // Show top 8 for donut chart
    const totalValue = products.reduce(
      (sum, product) => sum + (product.total_inventory_value || 0),
      0,
    )

    const pieData = products.map((product, index) => ({
      name: product.product_name,
      value: product.total_inventory_value || 0,
      itemStyle: {
        color: `hsl(${120 + index * 20}, 70%, 50%)`, // Green gradient
      },
    }))

    return {
      series: [
        {
          name: 'Inventory Value',
          type: 'pie',
          radius: ['40%', '70%'],
          data: pieData,
          label: {
            show: true,
            formatter: '{b}: {d}%',
            fontSize: 12,
          },
          labelLine: {
            show: true,
          },
        },
      ],
      totalValue: totalValue,
    }
  }, [data])

  const chartOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: { data: { name: string; value: number }[] }) => {
          const dataPoint = params[0]
          const product = data.find((p) => p.product_name === dataPoint.name)
          const imageHtml = product?.variations?.[0]?.image
            ? `<img src="${product.variations[0].image}" style="width: 40px; height: 40px; border-radius: 4px; margin-right: 8px; float: left;" />`
            : ''

          const variationsHtml =
            product?.variations
              ?.map(
                (variation) =>
                  `<div style="margin: 4px 0; padding: 4px; background: rgba(0,0,0,0.05); border-radius: 3px;">
              <strong>${variation.sku}</strong> - ${
                    variation.current_stock
                  } units<br/>
              <small>Price: ₨${variation.sell_price.toLocaleString()}</small>
            </div>`,
              )
              .join('') || ''

          return `
            <div style="padding: 8px; max-width: 300px;">
              ${imageHtml}
              <strong>${dataPoint.name}</strong><br/>
              <strong>Total Stock: ${dataPoint.value.toLocaleString()}</strong><br/>
              <strong>Total Value: ₨${(
                product?.total_inventory_value || 0
              ).toLocaleString()}</strong><br/>
              <hr style="margin: 8px 0; border: 1px solid #eee;">
              <strong>Variations:</strong><br/>
              ${variationsHtml}
            </div>
          `
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => value.toLocaleString(),
        },
      },
      yAxis: {
        type: 'category',
        data: barChartData.yAxis,
        axisLabel: {
          interval: 0,
          formatter: (value: string) => {
            return value.length > 20 ? `${value.substring(0, 20)}...` : value
          },
        },
      },
      series: barChartData.series,
    }),
    [barChartData],
  )

  const donutOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: {
          name: string
          value: number
          percent: number
        }) => `
          <div style="padding: 8px;">
            <strong>${params.name}</strong><br/>
            Value: $${params.value.toLocaleString()}<br/>
            Percentage: ${params.percent}%
          </div>
        `,
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'center',
        textStyle: {
          fontSize: 12,
        },
      },
      series: donutChartData.series,
    }),
    [donutChartData],
  )

  if (loading) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText>Loading inventory data...</DcText>
      </DcDiv>
    )
  }

  if (error) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText color={colors.danger}>Error loading data: {error}</DcText>
      </DcDiv>
    )
  }

  // Show only bar chart
  if (showBarChartOnly) {
    return (
      <DcChart
        type="horizontalBar"
        data={barChartData}
        options={chartOptions}
        height={300}
        width="100%"
        title="Top Products by Stock Count"
        isLoading={loading}
        isTitleClickable={!!onTitleClick}
        onClickTitle={onTitleClick}
      />
    )
  }

  // Show both (original layout)
  return (
    <DcDiv>
      <DcFlexBox gap="20px" height="auto">
        {/* Primary Chart - Horizontal Bar Chart */}
        <DcDiv style={{ width: '60%' }}>
          <DcChart
            type="horizontalBar"
            data={barChartData}
            options={chartOptions}
            height={400}
            width="100%"
            title="Top Products by Stock Count"
            isLoading={loading}
            isTitleClickable={!!onTitleClick}
            onClickTitle={onTitleClick}
          />
        </DcDiv>

        {/* Secondary Chart - Donut Chart */}
        <DcDiv style={{ width: '40%' }}>
          <DcDiv style={{ position: 'relative' }}>
            <DcChart
              type="pie"
              data={donutChartData}
              options={donutOptions}
              height={300}
              width="100%"
              title="Inventory Value Distribution"
              isLoading={loading}
              isTitleClickable={!!onTitleClick}
              onClickTitle={onTitleClick}
            />
            {/* Center total value display */}
            <DcDiv
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}
            >
              <DcText
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: colors.success,
                }}
              >
                ₨{donutChartData.totalValue?.toLocaleString() || '0'}
              </DcText>
              <DcText
                style={{
                  fontSize: '12px',
                  color: colors.grayMedium,
                }}
              >
                Total Value
              </DcText>
            </DcDiv>
          </DcDiv>
        </DcDiv>
      </DcFlexBox>
    </DcDiv>
  )
}

export default MostCountChart
